import { HOME_INSIGHT_MODE_ORDER } from '../constants/guestVerseInsightModes';
import {
  guestInsightSnapshotFingerprint,
  guestInsightSnapshotForMode,
  guestInsightUserMessage,
} from '../utils/guestInsightPayload';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const memoryCache = new Map();
const STORAGE_PREFIX = 'bf_guest_insight:';

function cacheKey({ bookId, chapter, verse, mode, translation, snapshotFingerprint }) {
  const snap = snapshotFingerprint ?? '0';
  return `${bookId}:${chapter}:${verse}:${mode}:${translation || 'synodal'}:${snap}`;
}

function readStorage(key) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.text) return parsed;
  } catch {
    /* no-op */
  }
  return null;
}

function writeStorage(key, payload) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(payload));
  } catch {
    /* no-op */
  }
}

export function getCachedGuestVerseInsight(params) {
  const key = cacheKey(params);
  if (memoryCache.has(key)) return memoryCache.get(key);
  const stored = readStorage(key);
  if (stored) {
    memoryCache.set(key, stored);
    return stored;
  }
  return null;
}

function setCachedGuestVerseInsight(params, result) {
  const key = cacheKey(params);
  const entry = { text: result.text, mode: result.mode || params.mode };
  memoryCache.set(key, entry);
  writeStorage(key, entry);
}

function resolveRequestPayload({
  bookId,
  chapter,
  verse,
  mode,
  translation,
  language,
  message,
  snapshot,
  contextAnswerText,
  explainAnswerText,
}) {
  const priorAnswer = contextAnswerText ?? explainAnswerText;
  const lang = (language || localStorage.getItem('bibleLanguage') || 'ru').toLowerCase().slice(0, 2);
  const resolvedMessage = message ?? guestInsightUserMessage(mode);
  const resolvedSnapshot =
    snapshot !== undefined ? snapshot : guestInsightSnapshotForMode(mode, priorAnswer);
  const snapshotFingerprint = guestInsightSnapshotFingerprint(resolvedSnapshot);
  return {
    lang,
    body: {
      bookId,
      chapter,
      verse,
      mode,
      translation,
      language: lang,
      message: resolvedMessage || undefined,
      snapshot: resolvedSnapshot || undefined,
    },
    cacheParams: {
      bookId,
      chapter,
      verse,
      mode,
      translation,
      snapshotFingerprint,
    },
  };
}

/**
 * Гостевое толкование стиха без JWT (память + localStorage + серверный кэш).
 */
export async function fetchGuestVerseInsight({
  bookId,
  chapter,
  verse,
  mode,
  translation = 'synodal',
  language,
  message,
  snapshot,
  contextAnswerText,
  explainAnswerText,
  forceNetwork = false,
}) {
  const { lang, body, cacheParams } = resolveRequestPayload({
    bookId,
    chapter,
    verse,
    mode,
    translation,
    language,
    message,
    snapshot,
    contextAnswerText,
    explainAnswerText,
  });

  if (!forceNetwork) {
    const hit = getCachedGuestVerseInsight(cacheParams);
    if (hit?.text) return { ...hit, fromCache: true };
  }

  const response = await fetch(`${API_BASE}/verse/insight/guest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (response.status === 429 && data?.code === 'GUEST_INSIGHT_LIMIT_REACHED') {
    const err = new Error(data.error || 'Guest limit reached');
    err.code = 'GUEST_INSIGHT_LIMIT_REACHED';
    throw err;
  }
  if (!response.ok) {
    throw new Error(data?.error || 'Request failed');
  }
  const result = {
    text: typeof data?.text === 'string' ? data.text.trim() : '',
    mode: data?.mode || mode,
    fromCache: false,
  };
  if (result.text) setCachedGuestVerseInsight(cacheParams, result);
  return result;
}

/** Подгрузка режимов в фоне (для application передайте contextAnswerText — ответ simple). */
export function prefetchGuestVerseInsights({
  bookId,
  chapter,
  verse,
  translation,
  modes,
  contextAnswerText,
  explainAnswerText,
}) {
  const list = modes || HOME_INSIGHT_MODE_ORDER;
  const priorAnswer = contextAnswerText ?? explainAnswerText;
  list.forEach((mode) => {
    const { cacheParams } = resolveRequestPayload({
      bookId,
      chapter,
      verse,
      mode,
      translation,
      contextAnswerText: priorAnswer,
    });
    if (getCachedGuestVerseInsight(cacheParams)) return;
    void fetchGuestVerseInsight({
      bookId,
      chapter,
      verse,
      mode,
      translation,
      contextAnswerText: priorAnswer,
    }).catch(() => {});
  });
}
